import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Spinner from "react-bootstrap/Spinner";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  User,
} from "firebase/auth";
import { useIonToast, IonText } from "@ionic/react";
import { Link } from "react-router-dom";

import { auth } from "../../../lib/firebase";
import { useAuth } from "../../../providers/auth";
import Auth from "../../../api/auth";

import styles from "./styles.module.css";

type PasswordAuthProps = {
  registerLink: string;
  type?: "sign-in" | "sign-up";
  loading?: boolean;
  disabled?: boolean;
  error: string | null;
  onSuccess?: (user: User, provider?: "password" | "google") => void;
};

export default function PasswordAuth({
  registerLink,
  loading = false,
  type = "sign-in",
  disabled = false,
  error,
  onSuccess,
}: PasswordAuthProps) {
  const [show, setShow] = React.useState(false);
  const [requesting, setRequesting] = React.useState(false);
  const [formData, setFormData] = React.useState({
    email: "",
    password: "",
  });

  const { loading: firebaseLoading } = useAuth();

  const intl = useIntl();
  const [present] = useIonToast();

  const isSignIn = type === "sign-in";
  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const { value, id } = e.currentTarget;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [id]: value,
    }));
  };

  const handleForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setRequesting(true);

      const { data, err } = await Auth.approve({ email: formData.email, type });
      if (err || !data?.ok || !data?.approve) {
        if (data?.error) {
          const errorMessage = data.error[intl.locale] || data.error.en;
          present({
            message: errorMessage,
            duration: 5500,
            color: "danger",
            position: "top",
            cssClass: "error-toast",
          });
        }
        return;
      }

      if (isSignIn) {
        const { user } = await signInWithEmailAndPassword(
          auth,
          formData.email,
          formData.password,
        );
        if (user?.uid && onSuccess) {
          onSuccess(user);
        }
      } else {
        const { user } = await createUserWithEmailAndPassword(
          auth,
          formData.email,
          formData.password,
        );

        console.log("User created with email and password:", user);
        if (user?.uid && onSuccess) {
          onSuccess(user, "password");
        }
      }

      return;
    } catch (err: any) {
      if (err?.code?.includes("credential")) {
        return present({
          message: intl.formatMessage({ id: "auth.login.error" }),
          duration: 6000,
          color: "light",
          position: "top",
          cssClass: "error-toast",
        });
      }
      return present({
        message: intl.formatMessage({ id: "common.tryItLater" }),
        duration: 6000,
        color: "light",
        position: "top",
        cssClass: "error-toast",
      });
    } finally {
      setRequesting(false);
    }
  };

  const isLoading = requesting || loading;

  return (
    <form className={styles.authForm} onSubmit={handleForm}>
      <div className={styles.inputHolder}>
        <label className="form-label">
          <FormattedMessage id="auth.login.email" />
        </label>
        <input
          type="email"
          required
          placeholder="name@example.com"
          id="email"
          value={formData.email}
          onChange={(e) => handleChange(e)}
          className="form-control"
        />
      </div>
      <div className={`${styles.inputHolder} ${styles.passwordHolder}`}>
        <label className="form-label">
          <FormattedMessage id="auth.login.password" />
        </label>
        <input
          type={show ? "text" : "password"}
          required
          placeholder="Password"
          className="form-control"
          id="password"
          value={formData.password}
          onChange={(e) => handleChange(e)}
        />
        <span
          onClick={() => setShow(!show)}
          className={styles.togglePassword}
        ></span>
      </div>

      {!isSignIn && (
        <div className="color-step-500 fs-2 mt-3">
          <FormattedMessage id="auth.register.continuing" />{" "}
          <a
            href="https://www.visitoai.com/en/terms"
            target="_blank"
            rel="noreferrer"
          >
            <FormattedMessage id="auth.register.loginTerms" />
          </a>{" "}
          <FormattedMessage id="common.and" />{" "}
          <a
            href="https://www.visitoai.com/en/privacy"
            target="_blank"
            rel="noreferrer"
          >
            <FormattedMessage id="auth.register.loginPrivacy" />
          </a>
        </div>
      )}

      {error && (
        <p className="mt-1 mb-1">
          <IonText color="danger">{error}</IonText>
        </p>
      )}

      <button
        type="submit"
        className="btn btn-visito-primary w-100 py-8 mb-4 mt-3 rounded-2"
        disabled={disabled || firebaseLoading}
      >
        {isLoading ? (
          <Spinner animation="border" role="status" size="sm">
            <span className="visually-hidden">
              <FormattedMessage id="common.loading" />
              ...
            </span>
          </Spinner>
        ) : (
          <FormattedMessage
            id={isSignIn ? "auth.login.login" : "auth.register.register"}
          />
        )}
      </button>

      {isSignIn && (
        <div className="text-center">
          <div className="color-step-500">
            <FormattedMessage id="auth.login.forgotLabel" />{" "}
            <Link to="/auth/forgot-password">
              <FormattedMessage id="auth.login.forgotPassword" />
            </Link>
          </div>
        </div>
      )}

      <div className="text-center mt-4">
        <div className="color-step-500">
          <FormattedMessage
            id={
              isSignIn ? "auth.login.registerLabel" : "auth.register.loginLabel"
            }
          />
        </div>
        <div>
          {isSignIn ? (
            <Link to={registerLink}>
              <FormattedMessage
                id={isSignIn ? "auth.login.register" : "auth.register.login"}
              />
            </Link>
          ) : (
            <Link to={registerLink}>
              <FormattedMessage
                id={isSignIn ? "auth.login.register" : "auth.register.login"}
              />
            </Link>
          )}
        </div>
      </div>
    </form>
  );
}
